import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Switch } from '~/components/ui/switch';
import { Textarea } from '~/components/ui/textarea';
import { RecurrenceEditModeDialog, type RecurrenceEditMode } from '~/components/ui/RecurrenceEditModeDialog';
import LocalDateTime, { type IANATimezone } from '~/lib/localdatetime';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

interface CategoryScheduleEntryFormProps {
    storeId: number;
    categoryId: number;
    categoryName: string;
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editEntryId?: string;
    prefillDate?: string;
    selectedDay?: string;
}

const formatDate = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('yyyy-MM-dd') : '';
};

const formatTime = (date: LocalDateTime | null | undefined): string => {
    return date ? date.format('HH:mm') : '';
};

const parseDate = (dateStr: string, existingTime: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!dateStr) return null;
    const time = existingTime ? existingTime.format('HH:mm') : '00:00';
    return new LocalDateTime(`${dateStr} ${time}`, timezone);
};

const parseTime = (timeStr: string, existingDate: LocalDateTime | null, timezone: IANATimezone): LocalDateTime | null => {
    if (!timeStr || !existingDate) return null;
    const date = existingDate.format('yyyy-MM-dd');
    return new LocalDateTime(`${date} ${timeStr}`, timezone);
};

const WEEKDAY_LABELS: Record<string, string> = {
    MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So',
};

export function CategoryScheduleEntryForm({ storeId, categoryId, categoryName, open, onClose, onSuccess, editEntryId, prefillDate, selectedDay }: CategoryScheduleEntryFormProps) {
    const userTimezone = (useUserStore(state => state.user?.timezone) ?? 'Europe/Berlin') as IANATimezone;
    const userRole = useUserStore(state => state.user?.role);
    const canManageShiftEntries = userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;

    const [description, setDescription] = useState('');
    const [startTimeObj, setStartTimeObj] = useState<LocalDateTime | null>(() => {
        if (prefillDate) {
            return new LocalDateTime(`${prefillDate} 08:00`, userTimezone);
        }
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(1, 'hour').utc().toDate(), userTimezone);
    });
    const [endTimeObj, setEndTimeObj] = useState<LocalDateTime | null>(() => {
        if (prefillDate) {
            return new LocalDateTime(`${prefillDate} 17:00`, userTimezone);
        }
        return LocalDateTime.fromUTCDate(dayjs().tz(userTimezone).startOf('hour').add(2, 'hour').utc().toDate(), userTimezone);
    });
    const [isRecurring, setIsRecurring] = useState(false);
    const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY');
    const [interval, setIntervalValue] = useState(1);
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>({
        MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false,
    });
    const [recurrenceEndDate, setRecurrenceEndDate] = useState(dayjs().endOf('year').format('YYYY-MM-DD'));
    const [originalSeriesStart, setOriginalSeriesStart] = useState<LocalDateTime | null>(null);
    const [originalSeriesEnd, setOriginalSeriesEnd] = useState<LocalDateTime | null>(null);
    const [recurrenceEditDialogOpen, setRecurrenceEditDialogOpen] = useState(false);
    const [recurrenceEditDialogIsDelete, setRecurrenceEditDialogIsDelete] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<Record<string, any> | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    const { data: entryData, isLoading: isLoadingEntry, isFetching: isFetchingEntry } = trpc.categoryScheduleEntries.getOne.useQuery(
        { id: editEntryId! },
        { enabled: !!editEntryId, staleTime: 0 }
    );

    const createMutation = trpc.categoryScheduleEntries.create.useMutation();
    const updateMutation = trpc.categoryScheduleEntries.update.useMutation();
    const deleteMutation = trpc.categoryScheduleEntries.delete.useMutation();

    useEffect(() => {
        if (entryData && !dataLoaded && !isFetchingEntry) {
            setDescription(entryData.description ?? '');
            setIsRecurring(entryData.isRecurring);

            let seriesStart: LocalDateTime | null = null;
            let seriesEnd: LocalDateTime | null = null;

            if (entryData.startTimeLocal) {
                const stl = entryData.startTimeLocal as any;
                if (stl.localDatetime && stl.zone) {
                    seriesStart = new LocalDateTime(stl.localDatetime, stl.zone);
                }
            } else {
                const startDayjs = dayjs(entryData.startTime).tz(userTimezone);
                seriesStart = new LocalDateTime(startDayjs.format('YYYY-MM-DD HH:mm'), userTimezone);
            }

            if (entryData.endTimeLocal) {
                const etl = entryData.endTimeLocal as any;
                if (etl.localDatetime && etl.zone) {
                    seriesEnd = new LocalDateTime(etl.localDatetime, etl.zone);
                }
            } else {
                const endDayjs = dayjs(entryData.endTime).tz(userTimezone);
                seriesEnd = new LocalDateTime(endDayjs.format('YYYY-MM-DD HH:mm'), userTimezone);
            }

            setOriginalSeriesStart(seriesStart);
            setOriginalSeriesEnd(seriesEnd);

            if (entryData.isRecurring && selectedDay && seriesStart && seriesEnd) {
                const occurrenceStart = new LocalDateTime(`${selectedDay} ${seriesStart.format('HH:mm')}`, userTimezone);
                const occurrenceEnd = new LocalDateTime(`${selectedDay} ${seriesEnd.format('HH:mm')}`, userTimezone);
                setStartTimeObj(occurrenceStart);
                setEndTimeObj(occurrenceEnd);
            } else {
                if (seriesStart) setStartTimeObj(seriesStart);
                if (seriesEnd) setEndTimeObj(seriesEnd);
            }

            if (entryData.recurrenceRule) {
                const parts = entryData.recurrenceRule.split(';');
                for (const part of parts) {
                    const [key, val] = part.split('=');
                    if (key === 'FREQ') setFrequency(val as any);
                    if (key === 'INTERVAL') setIntervalValue(parseInt(val, 10));
                    if (key === 'BYDAY') {
                        const days = val.split(',');
                        setWeekdays(prev => {
                            const updated = { ...prev };
                            for (const d of days) updated[d] = true;
                            return updated;
                        });
                    }
                }
            }

            if (entryData.recurrenceEndDate) {
                setRecurrenceEndDate(dayjs(entryData.recurrenceEndDate).format('YYYY-MM-DD'));
            } else if (entryData.isRecurring) {
                setRecurrenceEndDate('');
            }

            setDataLoaded(true);
        }
    }, [entryData, dataLoaded, isFetchingEntry, userTimezone, selectedDay]);

    const derivedRecurrenceRule = useMemo(() => {
        if (!isRecurring) return '';
        let rule = `FREQ=${frequency};INTERVAL=${interval}`;
        if (frequency === 'WEEKLY') {
            const selectedDays = Object.entries(weekdays)
                .filter(([, v]) => v)
                .map(([k]) => k);
            if (selectedDays.length > 0) {
                rule += `;BYDAY=${selectedDays.join(',')}`;
            }
        }
        return rule;
    }, [isRecurring, frequency, interval, weekdays]);

    const handleSave = async () => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj) return;

        if (isRecurring && frequency === 'WEEKLY' && !Object.values(weekdays).some(v => v)) {
            toast.error("Error", { description: "Bitte mindestens einen Wochentag auswählen." });
            return;
        }

        if (editEntryId && isRecurring) {
            setPendingFormData({ action: 'update' });
            setRecurrenceEditDialogIsDelete(false);
            setRecurrenceEditDialogOpen(true);
            return;
        }

        executeSave('all');
    };

    const executeSave = async (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!startTimeObj || !endTimeObj) return;

        let submitStartTime = startTimeObj;
        let submitEndTime = endTimeObj;

        if (editMode === 'all' && originalSeriesStart && originalSeriesEnd) {
            submitStartTime = originalSeriesStart.clone().setHours(startTimeObj.getHours()).setMinutes(startTimeObj.getMinutes());
            submitEndTime = originalSeriesEnd.clone().setHours(endTimeObj.getHours()).setMinutes(endTimeObj.getMinutes());
        }

        setIsSaving(true);
        try {
            const payload = {
                categoryId,
                storeId,
                description: description || undefined,
                startTimeLocalSerialized: submitStartTime.serialize(),
                endTimeLocalSerialized: submitEndTime.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring ? (recurrenceEndDate ? new Date(recurrenceEndDate).toISOString() : undefined) : undefined,
            };

            if (editEntryId) {
                await updateMutation.mutateAsync({
                    id: editEntryId,
                    ...payload,
                    editMode,
                    selectedSlotDay: selectedDay ?? null,
                });
            } else {
                await createMutation.mutateAsync(payload);
            }
            onSuccess();
        } catch (e) {
            console.error('Error saving category entry:', e);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        if (!canManageShiftEntries) return;
        if (!editEntryId) return;
        if (isRecurring) {
            setPendingFormData({ action: 'delete' });
            setRecurrenceEditDialogIsDelete(true);
            setRecurrenceEditDialogOpen(true);
            return;
        }
        executeDelete('all');
    };

    const executeDelete = async (editMode: RecurrenceEditMode) => {
        if (!canManageShiftEntries) return;
        if (!editEntryId) return;
        try {
            await deleteMutation.mutateAsync({
                id: editEntryId,
                editMode,
                selectedSlotDay: selectedDay ?? null,
            });
            onSuccess();
        } catch (e) {
            console.error('Error deleting category entry:', e);
        }
    };

    const handleRecurrenceEditModeSelect = (mode: RecurrenceEditMode) => {
        setRecurrenceEditDialogOpen(false);
        if (pendingFormData?.action === 'delete') {
            executeDelete(mode);
        } else {
            executeSave(mode);
        }
        setPendingFormData(null);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-yellow-700">
                        {editEntryId ? 'Kategorie-Planung bearbeiten' : 'Neue Kategorie-Planung'}
                    </DialogTitle>
                </DialogHeader>

                {editEntryId && (isLoadingEntry || isFetchingEntry || !dataLoaded) ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (<>
                <div className="space-y-4 py-4">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                            Ausgewählt: <b>{categoryName}</b>
                        </span>
                    </div>
                    <div className="flex items-end gap-6">
                        <div className="space-y-2">
                            <Label>Datum *</Label>
                            <Input
                                type="date"
                                className="w-[140px]"
                                value={formatDate(startTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => {
                                    setStartTimeObj(parseDate(e.target.value, startTimeObj, userTimezone));
                                    setEndTimeObj(parseDate(e.target.value, endTimeObj, userTimezone));
                                }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Startzeit *</Label>
                            <Input
                                type="time"
                                className="w-[100px]"
                                value={formatTime(startTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => setStartTimeObj(parseTime(e.target.value, startTimeObj, userTimezone))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Endzeit *</Label>
                            <Input
                                type="time"
                                className="w-[100px]"
                                value={formatTime(endTimeObj)}
                                disabled={!canManageShiftEntries}
                                onChange={e => setEndTimeObj(parseTime(e.target.value, endTimeObj, userTimezone))}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Beschreibung (optional)</Label>
                        <Textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="z.B. Frühschicht, Spätschicht..."
                            rows={2}
                            disabled={!canManageShiftEntries}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            id="cat-is-recurring"
                            checked={isRecurring}
                            onCheckedChange={(checked) => setIsRecurring(Boolean(checked))}
                            disabled={!canManageShiftEntries || !!editEntryId}
                        />
                        <Label htmlFor="cat-is-recurring" className="cursor-pointer">Wiederkehrend</Label>
                    </div>

                    {isRecurring && (
                        <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Frequenz</Label>
                                    <Select value={frequency} onValueChange={(v) => setFrequency(v as any)} disabled={!canManageShiftEntries || !!editEntryId}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="DAILY">Täglich</SelectItem>
                                            <SelectItem value="WEEKLY">Wöchentlich</SelectItem>
                                            <SelectItem value="MONTHLY">Monatlich</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Intervall (alle x Tage/Wochen/Monate)</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={interval}
                                        onChange={e => setIntervalValue(parseInt(e.target.value, 10) || 1)}
                                        disabled={!canManageShiftEntries}
                                    />
                                </div>
                            </div>

                            {frequency === 'WEEKLY' && (
                                <div className="space-y-2">
                                    <Label>Wochentage</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(WEEKDAY_LABELS).map(([key, label]) => (
                                            <label key={key} className="flex items-center gap-1 text-sm">
                                                <Checkbox
                                                    checked={weekdays[key]}
                                                    onCheckedChange={(checked) =>
                                                        setWeekdays(prev => ({ ...prev, [key]: Boolean(checked) }))
                                                    }
                                                    disabled={!canManageShiftEntries}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                    {!Object.values(weekdays).some(selected => selected) && (
                                        <p className="text-xs text-destructive">Bitte mindestens einen Tag auswählen.</p>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Enddatum</Label>
                                <Input
                                    type="date"
                                    value={recurrenceEndDate}
                                    onChange={e => setRecurrenceEndDate(e.target.value)}
                                    disabled={!canManageShiftEntries}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    {canManageShiftEntries && editEntryId && (
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={isSaving || deleteMutation.isPending}
                            className="mr-auto"
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Löschen
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    {canManageShiftEntries && (
                        <Button
                            onClick={handleSave}
                            disabled={isSaving || !startTimeObj || !endTimeObj}
                            className="bg-yellow-600 hover:bg-yellow-700"
                        >
                            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editEntryId ? 'Speichern' : 'Erstellen'}
                        </Button>
                    )}
                </DialogFooter>
                <RecurrenceEditModeDialog
                    open={recurrenceEditDialogOpen}
                    isDelete={recurrenceEditDialogIsDelete}
                    onSelect={handleRecurrenceEditModeSelect}
                    onCancel={() => setRecurrenceEditDialogOpen(false)}
                    occurrenceLabel={startTimeObj?.format('EEEE, dd.MM.yyyy')}
                    occurrenceDate={startTimeObj?.format('dd.MM.yyyy')}
                    seriesStartDate={originalSeriesStart?.format('dd.MM.yyyy')}
                    seriesEndDate={recurrenceEndDate ? dayjs(recurrenceEndDate).format('DD.MM.YYYY') : undefined}
                />
                </>
                )}
            </DialogContent>
        </Dialog>
    );
}
